import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { decamelize } from '@ember/string';
import { type SafeString, htmlSafe } from '@ember/template';
import { isEmpty } from '@ember/utils';
import { cached, tracked } from '@glimmer/tracking';
import {
  type AppliedPropertyFilter,
  ContactIdsIntersection,
  type PropertyFilterInfoForQueryFilter,
  deserializeAppliedFilters,
  serializeAppliedFilters,
} from 'donor-elf-web/lib/filter-utils-new';
import objectUtils from 'donor-elf-web/lib/object-utils';
import { queryWithPages } from 'donor-elf-web/lib/store-utils';
import V2TaskModel from 'donor-elf-web/models/v2-task';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import ContactsExportService from 'donor-elf-web/services/contacts-export';
import FormatDateService from 'donor-elf-web/services/format-date-service';
import TaskService from 'donor-elf-web/services/task-service';
import FileSaver from 'file-saver';
import Papa from 'papaparse';

let DEFAULT_FILTER =
  '[{"propertyName":"completed","operation":"=","val":"Not Complete"}]';
let PAGE_SIZE = 50;

export default class TasksV2Controller extends Controller {
  @service declare contactsCache: ContactsCache;
  @service declare contactsExport: ContactsExportService;
  @service declare formatDateService: FormatDateService;
  @service declare media: any;
  @service declare taskService: TaskService;
  @service declare store: Store;

  queryParams = ['page', 'filters'];

  @tracked filters?: string;
  @tracked appliedFilters?: AppliedPropertyFilter[];
  @tracked page = 1;
  @tracked sortBy = 'dueDate';
  @tracked sortDir = 'asc';
  @tracked tasks: V2TaskModel[] = [];

  @tracked totalCount = 0;
  @tracked totalPages = 0;

  @tracked loading = true;
  @tracked downloading = false;
  @tracked downloadComplete = false;
  @tracked downloadPercentStyle?: SafeString;

  @action
  templateDidInsert() {
    if (isEmpty(this.filters)) {
      this.filters = DEFAULT_FILTER;
    }
    this.deserializeFilters();
    this.load();
  }

  @action
  templateWillDestroy() {
    this.tasks = [];
    this.page = 1;
    this.sortBy = 'dueDate';
    this.sortDir = 'asc';
    this.loading = true;
    this.filters = undefined;
    this.appliedFilters = undefined;
    this.downloading = false;
    this.downloadComplete = false;
    this.downloadPercentStyle = undefined;
  }

  async load() {
    this.loading = true;
    let queryFilter: any = {
      offset: (this.page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
    };

    let { availablePropertyFilters } = this;
    for (let appliedFilter of this.appliedFilters || []) {
      let propertyFilter = availablePropertyFilters.find(
        (x) => x.propertyName === appliedFilter.propertyName,
      )!;
      await propertyFilter.applyToQueryFilter(appliedFilter, queryFilter);
    }
    // if the filter excludes all contacts so pass in an invalid contact id to get no results
    if (queryFilter.contactId && queryFilter.contactId.length === 0) {
      queryFilter.contactId = ['none'];
    }

    let sort = decamelize(this.sortBy);
    if (this.sortDir === 'desc') {
      sort = `-${sort}`;
    }

    let records = await this.store.query('v2-task', {
      filter: objectUtils.decamelizeKeys(queryFilter),
      sort: sort,
    });
    this.totalCount = Number(records.meta?.total_count || 0);
    this.totalPages = Math.ceil(this.totalCount / PAGE_SIZE);
    this.tasks = records.slice();
    this.loading = false;
  }

  @cached
  get availablePropertyFilters(): PropertyFilterInfoForQueryFilter[] {
    let filters: PropertyFilterInfoForQueryFilter[] = [];
    filters.push({
      propertyName: 'id',
      label: 'Task ID',
      propertyType: 'number',
      availableOperations: ['='],
      systemOnly: true,
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.id = appliedFilter.val;
      },
    });
    filters.push({
      propertyName: 'completed',
      label: 'Completed Status',
      propertyType: 'string',
      listOptions: ['Completed', 'Not Complete'],
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.completed = appliedFilter.val;
      },
    });
    filters.push({
      propertyName: 'completedDate',
      label: 'Completed Date',
      propertyType: 'date',
      availableOperations: ['>=', '<=', '='],
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        if (appliedFilter.operation === '>=') {
          queryFilter.completed_from = appliedFilter.val;
        } else if (appliedFilter.operation === '<=') {
          queryFilter.completed_to = appliedFilter.val;
        } else {
          queryFilter.completed_from = appliedFilter.val;
          queryFilter.completed_to = appliedFilter.val;
        }
      },
    });
    filters.push({
      propertyName: 'due',
      label: 'Due',
      propertyType: 'string',
      listOptions: [
        {
          label: 'Today and Overdue',
          val: 'today-and-overdue',
        },
        {
          label: 'Overdue',
          val: 'overdue',
        },
        {
          label: 'Today',
          val: 'today',
        },
        {
          label: 'This Week',
          val: 'this-week',
        },
        {
          label: 'Upcoming',
          val: 'upcoming',
        },
        {
          label: 'Within a Month',
          val: 'within-a-month',
        },
        {
          label: 'No Due Date',
          val: 'none',
        },
      ],
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.due = appliedFilter.val;
      },
    });
    filters.push({
      propertyName: 'action',
      label: 'Action',
      propertyType: 'list',
      availableOperations: ['any'],
      listOptions: this.taskService.actions.map((x) => x.label),
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        let actionLabels = appliedFilter.val as string[];
        queryFilter.actionId = actionLabels.map(
          (x) => this.taskService.actionsByLabel[x].id,
        );
      },
    });
    filters.push({
      propertyName: 'medium',
      label: 'Medium',
      propertyType: 'list',
      availableOperations: ['any'],
      listOptions: this.taskService.mediums.map((x) => x.label),
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        let mediumLabels = appliedFilter.val as string[];
        queryFilter.mediumId = mediumLabels.map(
          (x) => this.taskService.mediumsByLabel[x].id,
        );
      },
    });
    filters.push({
      propertyName: 'contactIds',
      label: 'Contact',
      propertyType: 'contactIds',
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.contactId = ContactIdsIntersection(
          queryFilter.contactId,
          appliedFilter.val as string[],
        );
      },
    });

    for (let filterInfo of this.contactsCache.availablePropertyFiltersForContacts.filter(
      (x) => x.custom.includeOnAnyReport,
    )) {
      filters.push({
        propertyName: filterInfo.propertyName,
        label: `Contact ${filterInfo.label}`,
        propertyType: filterInfo.propertyType,
        availableOperations: filterInfo.availableOperations,
        listOptions: filterInfo.listOptions,
        listIsSingleSelection: filterInfo.listIsSingleSelection,
        applyToQueryFilter: async (
          appliedFilter: AppliedPropertyFilter,
          queryFilter: any,
        ) => {
          let contactIds = (
            await this.contactsCache.filter([appliedFilter])
          ).map((x) => x.id);
          queryFilter.contactId = ContactIdsIntersection(
            queryFilter.contactId,
            contactIds,
          );
        },
      });
    }
    return filters;
  }

  get actionsMenuItems() {
    return [
      {
        label: 'Download',
        svgJar: 'download',
        onClick: this.download,
      },
    ];
  }

  @action
  handleFiltersChanged(currentFilters: AppliedPropertyFilter[]) {
    this.page = 1;
    this.appliedFilters = currentFilters;
    this.serializeFilters();
    this.load();
  }

  serializeFilters() {
    this.filters = serializeAppliedFilters(this.appliedFilters);
  }

  deserializeFilters() {
    this.appliedFilters = deserializeAppliedFilters(this.filters);
  }

  get displayOneColumn() {
    return this.media.isMobile;
  }

  @action
  handleTaskDeleted(task: V2TaskModel) {
    this.tasks = this.tasks.filter((x) => x.id !== task.id);
  }

  @action
  changePage(pageNumber: number) {
    this.page = pageNumber;
    this.load();
  }

  @action
  finishedWithDownload() {
    this.downloading = false;
    this.downloadComplete = false;
  }

  @action
  async download() {
    this.downloading = true;
    this.downloadComplete = false;
    this.downloadPercentStyle = htmlSafe('width: 5%');
    let queryFilter: any = {};
    let { availablePropertyFilters } = this;
    for (let appliedFilter of this.appliedFilters || []) {
      let propertyFilter = availablePropertyFilters.find(
        (x) => x.propertyName === appliedFilter.propertyName,
      )!;
      await propertyFilter.applyToQueryFilter(appliedFilter, queryFilter);
    }
    // if the filter excludes all contacts so pass in an invalid contact id to get no results
    if (queryFilter.contactId && queryFilter.contactId.length === 0) {
      queryFilter.contactId = ['none'];
    }

    let downloadedTasks = await queryWithPages<V2TaskModel>({
      store: this.store,
      modelName: 'v2-task',
      queryOptions: { filter: queryFilter },
      pageSize: 100,
      pageCallback: (args) => {
        let percentageDownloaded = Math.round(
          (args.downloadedCount * 100) / args.totalCount,
        );
        if (percentageDownloaded < 5) {
          percentageDownloaded = 5;
        }
        this.downloadPercentStyle = htmlSafe(`width: ${percentageDownloaded}%`);
      },
    });
    this.downloadPercentStyle = htmlSafe(`width: 100%`);

    let headerRow = [
      'Name',
      'Action',
      'Medium',
      'Summary',
      'Notes',
      'Due',
      'Completed',
      '',
    ];
    let headerInfo = await this.contactsExport.addContactsHeaderColumns(
      headerRow,
      this.contactsCache.contacts,
    );
    let rows = [headerRow];
    for (let task of downloadedTasks) {
      let contact = this.contactsCache.contactsById[task.contactId!];
      let row = [
        contact?.name,
        this.taskService.actionLabelsById[task.actionId!],
        this.taskService.mediumLabelsById[task.mediumId!],
        task.summary,
        task.notes,
        this.formatDateService.formatForFile(task.dueDate),
        this.formatDateService.formatForFile(task.completedDate),
        '',
      ];
      await this.contactsExport.addContactInfoToRow(row, contact, headerInfo);
      rows.push(row);
    }

    this.store.createRecord('contact-download').save();

    let csv = Papa.unparse(rows);
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    FileSaver.saveAs(blob, 'tasks.csv', true);
    this.downloadComplete = true;
  }
}
